import { Node } from './node';
import { Grid } from './grid';
import { Graph } from './graph';

/**
 * The class Edge
 * describes the logical representation of an edge
 * in a graph or a tree.
 */
export class Edge {
    //Reference to the outgoing node.
    protected readonly from: Node; 
    //Reference to the incoming node.
    protected readonly to: Node;
    //Weight of the edge.
    protected weight: number;
    //Default Width(drawn on the canvas) of the edge.
    protected static readonly defaultLineWidth: number = 1;
    //Default color(drawn on the canvas) of the edge.
    protected static readonly defaultColor: string = "black";

    //Currrent color of the edge
    protected color: string;

    //Current width of th ege
    protected lineWidth: number;

    //Bezierpoints of the edge. Is null if not a bezier edge
    bezierPoint: BezierPoints;

    //Indicates if an edge is bézier or not.
    protected isBezier: boolean = false;

    constructor(from: Node, to: Node) {
        this.from = from;
        this.to = to;
        this.lineWidth = Edge.defaultLineWidth;
        this.color = Edge.defaultColor;
        this.bezierPoint = new BezierPoints(from, to);
    }
/**
 *  
 * @param isBezier if true , then the edge will be drawn as bezieredge
 * else as a normal straight line.
 */
    enableBezier(isBezier: boolean) {
        this.isBezier = isBezier;
    }

    /**
     * @returns true, if edge is drawn as Bézier. False, 
     * if edge is drawn as a straight line
     */
    isBezierEdge():boolean{
        return this.isBezier;
    }
    /**
    * 
    * @param color, set the color of the selected edge
    */
    setColor(color: string) {
        this.color = color;
    }

    /**
     * Sets the edge back to it's default color
     */
    setColorToDefault() {
        this.color = Edge.defaultColor;
    }
    /**
     * 
     * @param lineWidth, set the lineWidth of the selected edge
     */
    setLineWidth(lineWidth: number) {
        this.lineWidth = lineWidth;
    }

    /**
     * Sets the edge back to it's default linewidth
     */
    setLineWidthToDefault() {
        this.lineWidth = Edge.defaultLineWidth;
    }

    /**
     * Set color and linewidth to their default value
     */
    setToDefault() {
        this.color = Edge.defaultColor;
        this.lineWidth = Edge.defaultLineWidth;
    }

    getFromNode(): Node {
        return this.from;
    }

    getToNode(): Node {
        return this.to;
    }

    setWeight(weight: number) {
        this.weight = weight;
    }

    getWeight(): number {
        return this.weight;
    }


    /**
     * 
     * @param ctx reference to the context of the canvas
     * @param grid reference to the grid
     * 
     * This method is beeing called in the graph class
     */
    draw(ctx: CanvasRenderingContext2D, grid: Grid) {
        var centerFrom: [number, number] = grid.getCellCenter(this.from.getRowIndex(), this.from.getColIndex());
        var centerTo: [number, number] = grid.getCellCenter(this.to.getRowIndex(), this.to.getColIndex());

        if (!this.from.isVisible()) {
            centerFrom = [this.from.getShadowRawX(), this.from.getShadowRawY()];
            ctx.globalAlpha = 0.75;
        }

        if (!this.to.isVisible()) {
            centerTo = [this.to.getShadowRawX(), this.to.getShadowRawY()];
            ctx.globalAlpha = 0.75;
        }



        ctx.strokeStyle = this.color;
        ctx.lineWidth = this.lineWidth;
        ctx.beginPath();
        ctx.moveTo(centerFrom[0], centerFrom[1]);
        if (this.isBezier) {
            let correctRaw1: [number,number] = grid.convertCoordinateToRaw(
                this.bezierPoint.point1Coord[0], this.bezierPoint.point1Coord[1]
            );

            let correctRaw2: [number,number] = grid.convertCoordinateToRaw(
                this.bezierPoint.point2Coord[0], this.bezierPoint.point2Coord[1]
            );
            ctx.bezierCurveTo(correctRaw1[0],
                correctRaw1[1], correctRaw2[0],
                correctRaw2[1], centerTo[0], centerTo[1]);
        }else{
            ctx.lineTo(centerTo[0], centerTo[1]);
        }
        ctx.stroke();
        ctx.closePath();



        ctx.globalAlpha = 1;

      
    }

    //Draws both bezierpoints while bezierPoint should be visible (when setting them)
    drawBezierPoint(ctx: CanvasRenderingContext2D, grid: Grid){
        if (this.bezierPoint.visible) {
            this.bezierPoint.draw(ctx, grid);
        }
    }

    //Used for debugging
    public toString = (): string => {
        return "Edge: from:" + this.from.getName() + "," + this.to.getName();
    }

    //Should calculate the intersection between the edge and the targetting node
    intersection(b_x, b_y, p_x, p_y, ctx: CanvasRenderingContext2D, grid: Grid):[number,number] {
        if (b_x - p_x == 0) {
            return [p_x,p_y];
        }

        //Compute the linear function from the second beziercontrolpoint to the center of the targetnode
        let a = (b_y - p_y) / (b_x - p_x);
        let b = p_y - (a * p_x);
        let c = p_x;
        let d = p_y;
        let r = grid.getHeightRadius();

        let A = 1 + (a * a);
        let B = (2 * a * b) - (2 * a * d) - (2 * c);
        let C = (b * b) + (c * c) + (d * d) - (r * r) - (2 * b * d);

        //CHeck determinant
        let det = (B * B) - (4 * A * C);
        if (det < 0) {
            return;
        }

        //Compute both intersection of the linear function and the circle
        let x1 = (-B + Math.sqrt(det)) / (2 * A)
        let x2 = (-B - Math.sqrt(det)) / (2 * A);

        let y1 = a * x1 + b;
        let y2 = a * x2 + b;


        let defX = 0;
        let defY = 0;

        let dist1: number = (b_x - x1) * (b_x - x1) + (b_y - y1) * (b_y - y1);
        let dist2: number = (b_x - x2) * (b_x - x2) + (b_y - y2) * (b_y - y2);

        //Choose the closer intersection (to the beziercontrolpoint)
        if (dist1 > dist2) {
            defX = x2;
            defY = y2;
        } else {
            defX = x1;
            defY = y1;
        }

        return [defX, defY];
    }

    /**
     * returns the coordinate on the bézier edge given [sx,sy]: coordinates of 
     * the startNode. [ex,ey]: coordiantes of the endNode. [cp1x,cp2y]: coordinates of
     * the endNode. [t] should be chosen from a range [0,1]. @param t defines the advancement of the coordiante
     * t = 1, returns the coordinate at the receiving end of the edge.
     * t = 0, returns the coordinate at the outgoing end of the edge.
     * t = 0.5, return the coodinate at the middle of the edge.
     * 
     * This is the formula of the bézier polynom of order 4
     */
    getBezierCoordinates(t:number, sx:number, sy:number, cp1x:number,
         cp1y: number, cp2x:number, cp2y: number, ex: number, ey: number) {
        return {
            x: Math.pow(1 - t, 3) * sx + 3 * t * Math.pow(1 - t, 2) * cp1x + 3 * t * t * (1 - t) * cp2x + t * t * t * ex,
            y: Math.pow(1 - t, 3) * sy + 3 * t * Math.pow(1 - t, 2) * cp1y + 3 * t * t * (1 - t) * cp2y + t * t * t * ey
        };
    }

    
    /**
     * returns the angle on the bézier edge given [sx,sy]: coordinates of 
     * the startNode. [ex,ey]: coordiantes of the endNode. [cp1x,cp2y]: coordinates of
     * the endNode. [t] should be chosen from a range [0,1]. @param t defines the advancement of the coordiante
     * t = 1, returns the coordinate at the receiving end of the edge.
     * t = 0, returns the coordinate at the outgoing end of the edge.
     * t = 0.5, return the coodinate at the middle of the edge.
     * 
     * This is the derivation of the bézier polynom of order 4, which is used to compute
     * the angle from the edge at the coordinate on percentage t to the incoming node.
     */
    getBezierAngle(t: number, sx: number, sy: number, cp1x: number,
         cp1y: number, cp2x: number, cp2y: number, ex: number, ey: number) {
        let dx = Math.pow(1 - t, 2) * (cp1x - sx) + 2 * t * (1 - t) * (cp2x - cp1x) + t * t * (ex - cp2x);
        let dy = Math.pow(1 - t, 2) * (cp1y - sy) + 2 * t * (1 - t) * (cp2y - cp1y) + t * t * (ey - cp2y);
        return -Math.atan2(dx, dy) + 0.5 * Math.PI;
    }
}

/**
 * This class stores the attributes for the bézier control points
 */
export class BezierPoints {
    //Coordinate of the first bézier control point
    point1Coord: [number,number] = [0,0];

    //Coordiante of the second bézier control point
    point2Coord: [number, number] = [0,0];

    //Reference to the startNode of the edge
    startNode: Node;

    //Reference to the endNode of the edge
    endNode: Node;
    visible: boolean = false;
    private initedBezier: boolean = false;

    constructor(startNode: Node, endNode: Node) {
        this.startNode = startNode;
        this.endNode = endNode;
    }

    initBezier(point1: [number, number], point2: [number, number], grid: Grid) {          
        this.initedBezier = true;
        this.point1Coord = this.getCoordinatesPoint1(point1[0], point1[1],grid);
        this.point2Coord = this.getCoordinatesPoint2(point2[0], point2[1], grid);

    }


    /**
     * Draws the bézier control points. Called in the draw-method of the edge class
     */
    draw(ctx: CanvasRenderingContext2D, grid: Grid) {

        if (this.visible) {
            var centerFrom: [number, number] = grid.getCellCenter(this.startNode.getRowIndex(), this.startNode.getColIndex());
            var centerTo: [number, number] = grid.getCellCenter(this.endNode.getRowIndex(), this.endNode.getColIndex());


            if (this.initedBezier == false) {
                this.changePoint1((centerFrom[0] + centerTo[0]) / 2, (centerFrom[1] + centerTo[1]) / 2,grid);
                this.changePoint2((centerFrom[0] + centerTo[0]) / 2, (centerFrom[1] + centerTo[1]) / 2,grid);                
                this.initedBezier = true;
            }

            ctx.globalAlpha = 0.4;
            ctx.fillStyle = "blue";

            let correctRaw1: [number,number] = grid.convertCoordinateToRaw(
                this.point1Coord[0], this.point1Coord[1]
            );

            let correctRaw2: [number,number] = grid.convertCoordinateToRaw(
                this.point2Coord[0], this.point2Coord[1]
            );

            ctx.moveTo(centerFrom[0], centerFrom[1]);
            ctx.lineTo(correctRaw1[0], correctRaw1[1]);
            ctx.stroke();

            ctx.beginPath();
            ctx.ellipse(correctRaw1[0], correctRaw1[1],
                grid.getBezierRadius(), grid.getBezierRadius(), 0, 0, 2 * Math.PI);
            ctx.stroke();
            ctx.fill();
            ctx.closePath();

            ctx.fillStyle = "red";

            ctx.moveTo(centerTo[0], centerTo[1]);
            ctx.lineTo(correctRaw2[0], correctRaw2[1]);
            ctx.stroke();

            ctx.beginPath();
            ctx.ellipse(correctRaw2[0], correctRaw2[1],
                grid.getBezierRadius(), grid.getBezierRadius(), 0, 0, 2 * Math.PI);
            ctx.stroke();
            ctx.fill();
            ctx.closePath();

            ctx.globalAlpha = 1;
        }
    }

    /**
     * 
     * @param rawX raw X coordinates of the grid
     * @param rawY raw Y coordinates of the grid
     * @param grid 
     * returns true if coordinates hit the coordinates of the bézier point
     */
    inRadiusPoint1(rawX: number, rawY: number, grid: Grid): boolean {
        var reelCoord: [number,number] = grid.convertCoordinateToRaw(
            this.point1Coord[0], this.point1Coord[1]);
        var x: number = reelCoord[0];
        var y: number = reelCoord[1];

        var distance: number = Math.sqrt(Math.pow(rawX - x, 2) + Math.pow(rawY - y, 2));
        if (distance <= grid.getBezierRadius())
            return true;

        return false;
    }

    
    /**
     * 
     * @param rawX raw X coordinates of the grid
     * @param rawY raw Y coordinates of the grid
     * @param grid reference to the grid
     * returns true if coordinates hit the coordinates of the bézier point
     */
    inRadiusPoint2(rawX: number, rawY: number, grid: Grid): boolean {
        
        var reelCoord: [number,number] = grid.convertCoordinateToRaw(
            this.point2Coord[0], this.point2Coord[1]);
        var x: number = reelCoord[0];
        var y: number = reelCoord[1];

        var distance: number = Math.sqrt(Math.pow(rawX - x, 2) + Math.pow(rawY - y, 2));
        if (distance <= grid.getBezierRadius())
            return true;

        return false;
    }

    /**
     * 
     * @param rawX new x coordinate
     * @param rawY new y coordinate
     * @param grid reference to the grid
     * 
     * Moves the first bézier control point to the new x,y-coordinate.
     * Called on drag-and-drop in the builder classes
     */
    changePoint1(rawX: number, rawY: number, grid: Grid) {
        this.point1Coord = this.getCoordinatesPoint1(rawX, rawY,grid);
    }

    /**
     * 
     * @param rawX new x coordinate
     * @param rawY new y coordinate
     * @param grid reference to the grid
     * 
     * Moves the second bézier control point to the new x,y-coordinate.
     * Called on drag-and-drop in the builder classes
     */
    changePoint2(rawX: number, rawY: number, grid: Grid) {
        this.point2Coord = this.getCoordinatesPoint2(rawX, rawY, grid);
    }

    getCoordinatesPoint1(xRaw: number, yRaw: number ,grid: Grid): [number, number] {
        return grid.getRawCellCoordinates(xRaw, yRaw);
    }
    getCoordinatesPoint2(xRaw: number, yRaw: number,grid: Grid): [number, number] {
        return grid.getRawCellCoordinates(xRaw, yRaw);
    }

    






}